import { useEffect, useState } from "react";
import axios from "axios";
import { X, Copy, Check, Building2, ShieldCheck, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmt as fmtMoney } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BankTransferModal — shows live IBAN / SWIFT / Beneficiary fetched from the
 * backend, generates a unique TR-BT-XXXX reference on confirm, and points the
 * customer at orders@toothrocket.com for receipt forwarding.
 *
 * Props:
 *   amountQar (number, required)        — amount to wire
 *   packId (string, optional)            — for credit-pack purchases
 *   invoiceId (string, optional)         — for invoice payments
 *   onClose ()                           — close handler
 *   onConfirmed (intent)                 — fired after backend accepts the intent
 */
export default function BankTransferModal({ amountQar, packId, invoiceId, onClose, onConfirmed }) {
    const [details, setDetails] = useState(null);
    const [intent, setIntent] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        axios.get(`${API}/payments/bank-transfer/details`)
            .then(({ data }) => setDetails(data))
            .catch(() => setDetails({ configured: false }));
    }, []);

    const copy = (key, value) => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        });
    };

    const confirm = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/payments/bank-transfer/intent`, {
                pack_id: packId || null,
                invoice_id: invoiceId || null,
                amount_qar: amountQar,
            });
            setIntent(data);
            onConfirmed?.(data);
            toast.success(`Wire reference generated: ${data.reference}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not generate transfer reference");
        } finally { setBusy(false); }
    };

    const d = intent?.bank_details || details || {};
    const ref = intent?.reference;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-5" data-testid="bank-transfer-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg p-7 relative max-h-[80vh] overflow-y-auto tr-modal-card">
                <button onClick={onClose} className="absolute top-4 end-4 text-slate-400 hover:text-slate-700" data-testid="bank-transfer-close">
                    <X size={18} />
                </button>
                <div className="flex items-center gap-2 mb-1">
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <Building2 size={18} />
                    </div>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10.5px] font-semibold tracking-wide uppercase">Preferred</span>
                </div>
                <div className="tr-overline mt-2">· Bank transfer</div>
                <div className="font-display text-2xl text-slate-900 tracking-tight">Wire {fmtMoney(amountQar, "QAR")}</div>
                <div className="text-[13px] text-slate-500 mt-1">
                    Funds usually clear within 1 business day. Reference must match the wire narration.
                </div>

                {!details && (
                    <div className="text-center text-slate-500 py-6"><Loader2 className="inline animate-spin" /></div>
                )}

                {details && (
                    <div className="mt-5 space-y-2.5">
                        <Row label="Beneficiary" value={d.beneficiary_name} k="beneficiary" copy={copy} copied={copied} />
                        <Row label="Bank" value={d.bank_name} k="bank" copy={copy} copied={copied} />
                        <Row label="IBAN" value={d.iban || "(pending — admin has not filled this yet)"} k="iban" copy={copy} copied={copied} mono />
                        <Row label="SWIFT / BIC" value={d.swift} k="swift" copy={copy} copied={copied} mono />
                        {d.account_number ? <Row label="Account #" value={d.account_number} k="acct" copy={copy} copied={copied} mono /> : null}
                        {d.branch ? <Row label="Branch" value={d.branch} k="branch" copy={copy} copied={copied} /> : null}
                        <Row label="Reference" value={ref || "— click Confirm to generate —"} k="ref" copy={copy} copied={copied} mono highlight={!!ref} />
                    </div>
                )}

                <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12.5px] text-slate-700 flex items-start gap-2">
                    <Mail size={14} className="text-slate-500 mt-0.5 shrink-0" />
                    <div>
                        After wiring, email the SWIFT receipt to <a href={`mailto:${d.orders_inbox || "orders@toothrocket.com"}`} className="text-cyan-700 hover:underline">{d.orders_inbox || "orders@toothrocket.com"}</a> with the reference number above.
                    </div>
                </div>

                {details && !details.configured && !intent ? (
                    <div className="mt-3 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
                        <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                        <div>The IBAN field is pending. The admin will populate it shortly — you can still generate the reference and complete the wire once the IBAN is published.</div>
                    </div>
                ) : null}

                <div className="mt-6 flex gap-3">
                    {!intent ? (
                        <>
                            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-[13.5px]" data-testid="bank-transfer-cancel">
                                Cancel
                            </button>
                            <button onClick={confirm} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-[#0B1120] text-white hover:bg-slate-800 text-[13.5px] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="bank-transfer-confirm">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                Confirm & generate reference
                            </button>
                        </>
                    ) : (
                        <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-[13.5px]" data-testid="bank-transfer-done">
                            Done — I'll send the wire shortly
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, k, copy, copied, mono, highlight }) {
    const isCopied = copied === k;
    return (
        <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${highlight ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-slate-200"}`}>
            <div className="min-w-0 flex-1">
                <div className="text-[10.5px] font-mono-tr tracking-[0.16em] uppercase text-slate-500">{label}</div>
                <div className={`truncate text-[14px] text-slate-900 ${mono ? "font-mono-tr" : ""}`}>{value}</div>
            </div>
            <button
                onClick={() => copy(k, value)}
                disabled={!value || value.startsWith("(") || value.startsWith("—")}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded disabled:opacity-40"
                data-testid={`bank-transfer-copy-${k}`}
                aria-label={`Copy ${label}`}
            >
                {isCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
            </button>
        </div>
    );
}
